import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Form,
  Input,
  DatePicker,
  Checkbox,
  message,
  Tag,
  Popconfirm,
  Select,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import Box from '@iso/components/utility/box';
import PageHeader from '@iso/components/utility/pageHeader';
import { holidayService } from '@iso/services';
import { useSelector } from 'react-redux';
import moment from 'moment';
import HolidayManagementWrapper from './HolidayManagement.styles';
import Modal from '@iso/components/Feedback/Modal';
import IntlMessages from '@iso/components/utility/intlMessages';

const { Option } = Select;

const HolidayManagement = () => {
  const [form] = Form.useForm();
  const [holidays, setHolidays] = useState([]);
  const [customHolidays, setCustomHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const currentProperty = useSelector((state) => state.property.currentProperty);
  const [property, setProperty] = useState(null);

  useEffect(() => {
    // Get property from Redux or localStorage
    const storedProperty = localStorage.getItem('active_property');
    const activeProperty = currentProperty || (storedProperty ? JSON.parse(storedProperty) : null);

    if (activeProperty && activeProperty.id) {
      setProperty(activeProperty);
      loadHolidays(activeProperty.id);
    } else {
      message.error('No property selected. Please select a property first.');
    }
  }, [currentProperty, selectedYear]);

  const loadHolidays = async (propertyId) => {
    try {
      setLoading(true);
      const holidaysRes = await holidayService.getHolidays(propertyId, selectedYear);

      if (holidaysRes.code === '0000') {
        const allHolidays = holidaysRes.holidays || [];

        // Extract custom holidays for state tracking
        const customHols = allHolidays.filter(h => h.property_id !== null && h.property_id !== undefined);
        setCustomHolidays(customHols);

        // Sort by date
        allHolidays.sort((a, b) => moment(a.date).unix() - moment(b.date).unix());
        setHolidays(allHolidays);
      }
    } catch (error) {
      console.error('Error loading holidays:', error);
      message.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleAddHoliday = async (values) => {
    if (!property || !property.id) {
      message.error('No property selected');
      return;
    }

    try {
      setLoading(true);
      const data = {
        property_id: property.id,
        name: values.name,
        date: values.date.format('YYYY-MM-DD'),
        is_recurring: values.is_recurring || false,
      };

      const response = await holidayService.addCustomHoliday(data);

      if (response.code === '0000') {
        message.success('Custom holiday added successfully');
        setModalVisible(false);
        form.resetFields();
        loadHolidays(property.id);
      } else {
        message.error(response.message || 'Failed to add holiday');
      }
    } catch (error) {
      console.error('Error adding holiday:', error);
      message.error('Failed to add custom holiday');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (!property || !property.id) {
      message.error('No property selected');
      return;
    }

    try {
      setLoading(true);
      const response = await holidayService.deleteHoliday(holidayId);

      if (response.code === '0000') {
        message.success('Holiday deleted successfully');
        loadHolidays(property.id);
      } else {
        message.error(response.message || 'Failed to delete holiday');
      }
    } catch (error) {
      console.error('Error deleting holiday:', error);
      message.error('Failed to delete holiday');
    } finally {
      setLoading(false);
    }
  };

  const getHolidayType = (holiday) => {
    if (holiday.property_id) {
      return { color: 'blue', text: 'Custom' };
    } else if (holiday.state) {
      return { color: 'orange', text: `State (${holiday.state})` };
    } else {
      return { color: 'green', text: 'National' };
    }
  };

  const handleModalSubmit = () => {
    form.submit();
  }

  const cancelModal = () => {
    setModalVisible(false);
    form.resetFields();
  }

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => moment(date).format('DD MMM YYYY'),
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      key: 'type',
      render: (_, record) => {
        const type = getHolidayType(record);
        return <Tag color={type.color}>{type.text}</Tag>;
      },
      filters: [
        { text: 'National', value: 'national' },
        { text: 'State', value: 'state' },
        { text: 'Custom', value: 'custom' },
      ],
      onFilter: (value, record) => {
        if (value === 'national') return !record.state && !record.property_id;
        if (value === 'state') return !!record.state;
        if (value === 'custom') return !!record.property_id;
        return true;
      },
    },
    {
      title: 'Recurring',
      dataIndex: 'is_recurring',
      key: 'is_recurring',
      render: (isRecurring) => (isRecurring ? 'Yes' : 'No'),
      filters: [
        { text: 'Recurring', value: true },
        { text: 'One-time', value: false },
      ],
      onFilter: (value, record) => record.is_recurring === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        // Only show delete for custom holidays
        if (record.property_id) {
          return (
            <Popconfirm
              title="Are you sure you want to delete this holiday?"
              onConfirm={() => handleDeleteHoliday(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                Delete
              </Button>
            </Popconfirm>
          );
        }
        return '-';
      },
    },
  ];

  if (!property) {
    return (
      <LayoutWrapper>
        <PageHeader>Holiday Management</PageHeader>
        <Box>
          <Card>
            <p>No property selected. Please select a property from the properties list.</p>
          </Card>
        </Box>
      </LayoutWrapper>
    );
  }

  return (
    <HolidayManagementWrapper>
      <LayoutWrapper>
        <Box>
          <div className="page-header-wrapper">
            <h2 className="page-title">Holiday Calendar - {property.name}</h2>
          </div>

          <Card
            className="holiday-card"
            title={
              <div className="card-header">
                <span className="card-title">
                  <CalendarOutlined /> Holidays for {selectedYear}
                </span>
                <div className="card-actions">
                  <Select
                    value={selectedYear}
                    onChange={setSelectedYear}
                    className="year-select"
                  >
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() + i;
                      return (
                        <Option key={year} value={year}>
                          {year}
                        </Option>
                      );
                    })}
                  </Select>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setModalVisible(true)}
                    className="add-button"
                  >
                    Add Custom Holiday
                  </Button>
                </div>
              </div>
            }
          >
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card size="small" className="stat-card-wrapper">
                  <div className="stat-card">
                    <div className="stat-value">{holidays.filter(h => !h.state && !h.property_id).length}</div>
                    <div className="stat-label">National Holidays</div>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" className="stat-card-wrapper">
                  <div className="stat-card">
                    <div className="stat-value">{holidays.filter(h => h.state).length}</div>
                    <div className="stat-label">State Holidays</div>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" className="stat-card-wrapper">
                  <div className="stat-card">
                    <div className="stat-value">{holidays.filter(h => h.property_id).length}</div>
                    <div className="stat-label">Custom Holidays</div>
                  </div>
                </Card>
              </Col>
            </Row>

            <Table
              columns={columns}
              dataSource={holidays}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 15 }}
              className="holidays-table"
            />
          </Card>

          {/* Add Custom Holiday Modal */}
          <Modal
            title="Add Custom Holiday"
            open={modalVisible}
            onCancel={cancelModal}
            footer={[
              <Button key="back" onClick={cancelModal} className="cancel-button">
                {<IntlMessages id="propertyPage.modal.cancel" />}
              </Button>,
              <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  key="submit"
                  className="btn-success"
                  block
                  onClick={handleModalSubmit}
                >
                  {<IntlMessages id="propertyPage.modal.save" />}
                </Button>
            ]}
            width={500}
            className="holiday-modal"
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleAddHoliday}
            >
              <Form.Item
                label="Holiday Name"
                name="name"
                rules={[
                  { required: true, whitespace: true, message: 'Please enter holiday name' }
                ]}
              >
                <Input placeholder="e.g., Company Anniversary" />
              </Form.Item>

              <Form.Item
                label="Date"
                name="date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Form.Item>

              <Form.Item
                name="is_recurring"
                valuePropName="checked"
              >
                <Checkbox>
                  Recurring (applies every year)
                </Checkbox>
              </Form.Item>
            </Form>
          </Modal>
        </Box>
      </LayoutWrapper>
    </HolidayManagementWrapper>
  );
};

export default HolidayManagement;
